import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Paper, TextField, Menu, MenuItem } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import HomeIcon from '@material-ui/icons/Home';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import SaveIcon from '@material-ui/icons/Save';
import { GoogleMap, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import {
  RMSTextField,
  RMSSelect,
  RMSDateTimePicker,
} from '../../../shared/components/form-controls';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import SectionLabel from '../common/SectionLabel';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import {
  getAddressCoordinateId,
  getAddressCoordinateDetails,
  saveAddressCoordinates,
  linkAddressCoordinate,
} from '../../services/addressService';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import {
  DATUM_OPTIONS,
  DEFAULT_VALUES,
  DEFAULT_CENTER,
  DEFAULT_MENU_POS,
  CURRENT_MAP_OPTIONS,
} from '../../constants/addressCoordinatesFormConstants';
import { parseCoords, getCoords } from '../../utility/addressCoordinatesHelper';
import { saveUserSetting } from 'reducers/UserSettingsReducer';
import { mapNight, mapDay } from 'config/configureMap';

const AddressCoordinatesTab = forwardRef(
  ({ ptsAddressID, fullAddressText, onStateChange }, ref) => {
    const dispatch = useDispatch();
    const canEditAddress = usePermissions(PERMISSION_RESOURCES.ADDRESS).Edit;
    const isViewOnly = !canEditAddress;
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [coordinate, setCoordinate] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [searchBox, setSearchBox] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [addEventAnchor, setAddEventAnchor] = useState(null);
    const [menuPos, setMenuPos] = useState(DEFAULT_MENU_POS);
    const [coords, setCoords] = useState(null);

    const themeMode = useSelector((state) => state.theme.mode);
    const menuRef = useRef(null);
    const mapRef = useRef();
    const userSettings = useSelector((state) => state.userSettings);
    const userSettingsRef = useRef(userSettings);
    const zoomRef = useRef(userSettings?.mapSettings?.mapZoom || 8);
    const centerRef = useRef(
      { lat: userSettings?.mapSettings?.mapLat, lng: userSettings?.mapSettings?.mapLng } ||
        DEFAULT_CENTER
    );
    const mapOptionsRef = useRef(
      useSelector((state) => ({
        ...state.map.options,
        ...CURRENT_MAP_OPTIONS,
        styles: themeMode === 'day' ? mapDay : mapNight,
      }))
    );

    const { measureOptions } = useUserDictionaryOptions();

    const methods = useForm({
      mode: 'onSubmit',
      defaultValues: DEFAULT_VALUES,
      disabled: isViewOnly,
    });

    const { control, setValue } = methods;
    const [Latitude, Longitude] = useWatch({ control, name: ['Latitude', 'Longitude'] });
    const { isDirty } = useFormState({ control });
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const markerPosition = useMemo(
      () =>
        Latitude !== '' && Longitude !== '' && Latitude != null && Longitude != null
          ? { lat: Number(Latitude), lng: Number(Longitude) }
          : null,
      [Latitude, Longitude]
    );

    const centerMap = useCallback(() => {
      const { mapZoom, mapLat, mapLng } = userSettingsRef.current.mapSettings;
      zoomRef.current = mapZoom;
      centerRef.current = { lat: mapLat, lng: mapLng };
      if (mapRef.current) {
        mapRef.current.setZoom(mapZoom);
        mapRef.current.setCenter({ lat: mapLat, lng: mapLng });
      }
    }, []);

    const saveMapSettings = useCallback(() => {
      if (!mapRef.current) return;
      const zoom = mapRef.current.getZoom();
      const center = mapRef.current.getCenter().toJSON();
      const mapTypeId = mapRef.current.getMapTypeId();
      dispatch(
        saveUserSetting('mapSettings', {
          mapZoom: zoom,
          mapLat: center.lat,
          mapLng: center.lng,
          mapTypeId,
        })
      );
    }, [dispatch]);

    const onZoomChanged = useCallback(() => {
      if (!mapRef.current) return;
      zoomRef.current = mapRef.current.getZoom();
    }, []);

    const onCenterChanged = useCallback(() => {
      if (!mapRef.current) return;
      centerRef.current = mapRef.current.getCenter().toJSON();
    }, []);

    const MapSettingsControls = useCallback(
      () => (
        <>
          <RMSIconButton
            onClick={centerMap}
            icon={<HomeIcon />}
            className={'address-coordinates-tab__mapIconButton'}
          />
          <RMSIconButton
            onClick={saveMapSettings}
            icon={<SaveIcon />}
            className={'address-coordinates-tab__mapIconButton'}
          />
        </>
      ),
      [centerMap, saveMapSettings]
    );

    const onMapLoad = useCallback((map) => {
      mapRef.current = map;
      setMapInstance(map);
      mapRef.current.setZoom(userSettingsRef?.current?.mapSettings?.mapZoom || zoomRef.current);
      mapRef.current.setMapTypeId(userSettingsRef.current.mapSettings.mapTypeId);
      const controlButtonDiv = document.createElement('div');
      ReactDOM.render(<MapSettingsControls />, controlButtonDiv);
      map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(controlButtonDiv);
    }, []);

    const loadCoordinate = useCallback(async () => {
      if (!ptsAddressID) return;

      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const ptsCoordinateID = await getAddressCoordinateId(ptsAddressID);
          if (!ptsCoordinateID) return null;
          return getAddressCoordinateDetails(ptsCoordinateID);
        },
        onSuccess: (data) => {
          setCoordinate(data || null);
          methods.reset({
            ...DEFAULT_VALUES,
            ...getCoords(data),
            UncertaintyDistance: data?.UnCertaintyDistance ?? '',
            Datum: data?.Datum || '',
            CoordinateDT: data?.CoordinateDT || null,
            Altitude: data?.Altitude ?? '',
            AltitudeRefPoint: data?.AltitudeRefPoint ?? '',
            CellID: data?.CellIDText || '',
            SectorID: data?.CellSectorIDText || '',
            Speed: data?.Speed ?? '',
            AltitudeUnitMeasure: data?.AltitudeUnitMeasure || '',
            SpeedUnitMeasure: data?.SpeedUnitMeasure || '',
          });
        },
      });
    }, [ptsAddressID, dispatch, methods]);

    const handleMapClick = useCallback(
      (event) => {
        if (isViewOnly) return;
        if (!event?.pixel?.x) return;
        const screen = {
          x: event.pixel.x,
          y: event.pixel.y,
        };
        const coords = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        setMenuPos(screen);
        setCoords(coords);
        setAddEventAnchor(menuRef.current);
      },
      [isViewOnly]
    );

    const onPlacesChanged = useCallback(() => {
      if (isViewOnly) return;
      const places = searchBox?.getPlaces();
      const place = places?.[0];
      if (!place?.geometry?.location) return;

      setValue('Latitude', place.geometry.location.lat(), { shouldDirty: true });
      setValue('Longitude', place.geometry.location.lng(), { shouldDirty: true });
    }, [isViewOnly, searchBox, setValue]);

    const handleUseCurrentAddress = useCallback(() => setSearchText(fullAddressText || ''), [
      fullAddressText,
    ]);

    const handleShowCurrentLocation = useCallback(() => {
      if (mapInstance && markerPosition) mapInstance.panTo(markerPosition);
    }, [mapInstance, markerPosition]);

    const handleSearchTextChange = useCallback((event) => setSearchText(event.target.value), []);

    const onSubmit = useCallback(
      async (data) => {
        const payload = {
          ...parseCoords(data.Latitude, data.Longitude),
          UnCertaintyDistance: data.UncertaintyDistance || null,
          Datum: data.Datum || null,
          CoordinateDT: data.CoordinateDT || null,
          Altitude: data.Altitude || null,
          AltitudeRefPoint: data.AltitudeRefPoint || null,
          CellIDText: data.CellID || null,
          CellSectorIDText: data.SectorID || null,
          Speed: data.Speed || null,
          AltitudeUnitMeasure: data.AltitudeUnitMeasure || null,
          SpeedUnitMeasure: data.SpeedUnitMeasure || null,
          ptsCoordinateID: coordinate?.ptsCoordinateID || null,
        };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: async () => {
            const saved = await saveAddressCoordinates(payload);
            const ptsCoordinateID = saved?.ptsCoordinateID || payload.ptsCoordinateID;
            if (ptsCoordinateID) {
              await linkAddressCoordinate(ptsAddressID, ptsCoordinateID);
            }
          },
          onSuccess: () => {
            dispatch(notify('Coordinate saved successfully', 'success'));
            loadCoordinate();
          },
        });
      },
      [coordinate, ptsAddressID, dispatch, loadCoordinate]
    );

    const handleSave = methods.handleSubmit(onSubmit);

    const useCoords = () => {
      closeMenu();
      setValue('Latitude', coords.lat, { shouldDirty: true });
      setValue('Longitude', coords.lng, { shouldDirty: true });
    };

    const closeMenu = () => {
      setAddEventAnchor(null);
    };

    const renderAddEventMenu = () => (
      <>
        <div style={{ position: 'absolute', top: menuPos.y, left: menuPos.x }} ref={menuRef}></div>
        <Menu
          id="simple-menu"
          anchorEl={addEventAnchor}
          keepMounted
          open={Boolean(addEventAnchor)}
          onClose={closeMenu}>
          <MenuItem onClick={useCoords}>Use this location</MenuItem>
          <MenuItem onClick={closeMenu}>Close</MenuItem>
        </Menu>
      </>
    );

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => isDirtyRef.current,
        save: () => handleSave(),
      }),
      [handleSave]
    );

    useEffect(() => {
      loadCoordinate();
    }, [loadCoordinate]);

    useEffect(() => {
      onStateChange?.({
        coordinate,
        isDirty,
        submitting: submitting || loading,
        canSave: canEditAddress,
      });
    }, [coordinate, isDirty, submitting, loading, canEditAddress, onStateChange]);

    useEffect(() => {
      userSettingsRef.current = userSettings;
    }, [userSettings]);

    return (
      <Box className="rms-dialog-section address-coordinates-tab" mt={2}>
        <FormProvider {...methods}>
          <SectionLabel>{fullAddressText || 'Coordinates'}</SectionLabel>

          <Box mb={2} className="address-coordinates-tab__map">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={markerPosition || DEFAULT_CENTER}
              zoom={markerPosition ? 15 : 10}
              onLoad={onMapLoad}
              options={mapOptionsRef.current}
              onZoomChanged={onZoomChanged}
              onCenterChanged={onCenterChanged}
              onClick={handleMapClick}
              key={themeMode}>
              <StandaloneSearchBox onLoad={setSearchBox} onPlacesChanged={onPlacesChanged}>
                <Paper elevation={3} className="address-coordinates-tab__search-box">
                  <RMSIconButton
                    icon={<ArrowForwardIcon fontSize="small" />}
                    tooltip="Use current address"
                    disabled={!fullAddressText}
                    onClick={handleUseCurrentAddress}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Address Search"
                    value={searchText}
                    onChange={handleSearchTextChange}
                  />
                  <RMSIconButton
                    icon={<MyLocationIcon fontSize="small" />}
                    tooltip="Show current location"
                    disabled={!markerPosition}
                    onClick={handleShowCurrentLocation}
                  />
                </Paper>
              </StandaloneSearchBox>
              {markerPosition && <Marker position={markerPosition} />}
              {renderAddEventMenu()}
            </GoogleMap>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={3}>
              <RMSTextField
                name="Latitude"
                label="Latitude"
                type="number"
                inputProps={{ min: -90, max: 90 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="Longitude"
                label="Longitude"
                type="number"
                inputProps={{ min: -180, max: 180 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="UncertaintyDistance"
                label="Uncertainty Distance"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="Datum"
                label="Datum"
                options={DATUM_OPTIONS}
                {...getDictionarySelectProps()}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSDateTimePicker name="CoordinateDT" label="Coordinate Date Time" />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="Altitude"
                label="Altitude"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="AltitudeRefPoint"
                label="Point Of Reference"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="AltitudeUnitMeasure"
                label="Altitude Units"
                options={measureOptions}
                {...getDictionarySelectProps()}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="CellID" label="CellID" inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="SectorID" label="SectorID" inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="Speed" label="Speed" type="number" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="SpeedUnitMeasure"
                label="Speed Units"
                options={measureOptions}
                {...getDictionarySelectProps()}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Box>
    );
  }
);

export default AddressCoordinatesTab;
